/**
 * Replace spaces with dashes in a string (for URL slugs).
 */
export function replaceSpaceWithDash(str: string): string {
  return str?.replace(/\s+/g, "-") ?? "";
}

/**
 * Replace dashes with spaces in a string (for reading URL slugs).
 */
export function replaceDashWithSpace(str: string): string {
  return str?.replace(/-/g, " ") ?? "";
}

/**
 * Normalise a display name for equality comparison: trims the ends, lowercases,
 * and collapses internal whitespace runs to a single space. Use when matching a
 * value that arrives from an untrusted surface (e.g. a URL query param) against
 * a canonical list, so casing and stray spaces don't cause false misses.
 * e.g. normalizeName("  South   AFRICA ") === normalizeName("South Africa")
 */
export function normalizeName(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Sanitize a project name for use in URLs.
 */
export function sanitizeProjectURL(name: string): string {
  return name?.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "") ?? "";
}

// Weak / stop words dropped from slugs for SEO cleanliness.
const SLUG_STOP_WORDS = new Set([
  "a", "an", "and", "as", "at", "be", "but", "by", "for", "from", "in", "into", "is", "it",
  "of", "on", "or", "the", "to", "with", "via", "per", "vs", "are", "was", "were", "will",
  "this", "that", "these", "those", "than", "then", "so",
]);

// Builds the SEO-friendly slug used in inventory item page URLs (tours, packages, cruises).
// Lowercases + trims the name, removes special characters entirely (/, (, ), &, @, :, ... —
// keeps letters incl. accented ones, numbers and dashes), drops weak/stop words, joins words
// with dashes, then prepends the item id. e.g. buildItemSlug(123, 'H/S Nebu at the Bay') => '123-hs-nebu-bay'
export const buildItemSlug = (
  id: string | number | null | undefined,
  name: string | null | undefined,
): string => {
  let normalizedName = "";
  if (typeof name === "string") {
    const words = name
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s-]/gu, "") // drop special chars entirely (keeps letters, numbers, dashes)
      .split(/\s+/)
      .filter(Boolean);
    const meaningful = words.filter((w) => !SLUG_STOP_WORDS.has(w));
    // fall back to all words if removing stop words would leave nothing
    normalizedName = (meaningful.length ? meaningful : words)
      .join("-")
      .replace(/-+/g, "-") // collapse repeated dashes
      .replace(/^-+|-+$/g, ""); // trim leading/trailing dashes
  }
  if (id === null || id === undefined || id === "") return normalizedName;
  return normalizedName ? `${id}-${normalizedName}` : `${id}`;
};

// Extracts the item id from the start of a slug built by buildItemSlug.
// e.g. getIdFromSlug('123-nubian-village') => '123'
export const getIdFromSlug = (slug: string | null | undefined): string => {
  if (!slug || typeof slug !== "string") return "";
  return slug.split("-")[0];
};

// Builds a clean URL slug from an item's `pageUrl`:
//   1) trim  2) lowercase  3) strip special characters  4) spaces → dashes
// Keeps letters (incl. accented), numbers and dashes; collapses repeated
// dashes and trims stray edge dashes.
// e.g. slugifyPageUrl("  Nubian Village (Aswan) ") => "nubian-village-aswan"
export const slugifyPageUrl = (
  pageUrl: string | null | undefined,
): string => {
  if (typeof pageUrl !== "string") return "";
  return pageUrl
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "") // remove special characters
    .replace(/\s+/g, "-") // spaces → dashes
    .replace(/-+/g, "-") // collapse repeated dashes
    .replace(/^-+|-+$/g, ""); // trim leading/trailing dashes
};

/**
 * Normalise a photo/image URL for safe rendering.
 * Returns `undefined` when the input is empty or falsy.
 * Handles absolute URLs, protocol-relative URLs, and root-relative paths.
 */
export function normalizePhotoUrl(
  photo?: string | null,
): string | undefined {
  if (!photo) return undefined;

  const value = photo.trim();
  if (!value) return undefined;

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("//")
  ) {
    return value;
  }

  return value.startsWith("/") ? value : `/${value}`;
}

/**
 * Derive a human-readable platform name from a social-media profile URL, for
 * use as an accessible name / title on an otherwise icon-only link.
 * e.g. "https://www.facebook.com/central" => "Facebook". Falls back to
 * "social media" when the URL can't be parsed.
 */
export function socialPlatformLabel(url?: string | null): string {
  if (!url) return "social media";
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    const platform = host.split(".")[0];
    if (!platform) return "social media";
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  } catch {
    return "social media";
  }
}

/**
 * Fisher–Yates shuffle — returns a new array with elements in random order.
 */
export function shuffleArray<T>(items: T[]): T[] {
  const arr = [...items];

  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

/**
 * Strip HTML tags and decode common HTML entities from a string.
 * Optionally truncate to `maxLength` characters (adds "..." if truncated).
 */
export function stripHtml(html: string, maxLength?: number): string {
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ")
    .trim();
  if (maxLength && text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
}

/**
 * Strip HTML then truncate at a word boundary, appending "…" if shortened.
 */
export function truncate(text: string, maxLen: number): string {
  if (!text) return "";
  const clean = stripHtml(text);
  if (clean.length <= maxLen) return clean;
  return clean.slice(0, maxLen).replace(/\s+\S*$/, "") + "…";
}
